import * as mutationFns from "./mutations.fns";
import { CommitFn } from "../types";
import { SORTING_DIR } from "@/common/models";

export const Mutations = <S>(getLastState: () => S, commit: CommitFn<S>) => {
  const updateOneBy = <T>(stateKey: keyof S, by: keyof T) => {
    return (payload: T) => {
      const s = getLastState();
      commit({
        logName: `update on item in ${stateKey} by ${by}`,
        payload,
        state: {
          ...s,
          [stateKey]: mutationFns.updateOne<T>(by)(
            // @ts-ignore
            s[stateKey],
            payload
          ),
        },
      });
    };
  };

  const updateManyBy = <T>(stateKey: keyof S, by: keyof T) => {
    return (payload: T[]) => {
      const s = getLastState();
      commit({
        logName: `update on item in ${stateKey} by ${by}`,
        payload,
        state: {
          ...s,
          [stateKey]: mutationFns.updateMany<T>(by)(
            // @ts-ignore
            s[stateKey],
            payload
          ),
        },
      });
    };
  };

  const set = <K extends keyof S>(stateKey: K) => {
    return (payload: S[K]) => {
      const s = getLastState();
      commit({
        logName: `set ${stateKey}`,
        payload,
        state: {
          ...s,
          [stateKey]: payload,
        },
      });
    };
  };

  const removeBy = <T>(stateKey: keyof S, by: keyof T) => {
    return (payload: T) => {
      const s = getLastState();
      commit({
        logName: `update on item in ${stateKey} by ${by}`,
        payload,
        state: {
          ...s,
          [stateKey]: mutationFns.removeBy<T>(by)(
            // @ts-ignore
            s[stateKey],
            payload
          ),
        },
      });
    };
  };

  const always = <K extends keyof S>(stateKey: K, val: S[K]) => {
    return () => {
      const s = getLastState();
      commit({
        logName: `set ${stateKey} to ` + val,
        payload: val,
        state: {
          ...s,
          [stateKey]: val,
        },
      });
    };
  };

  const toggle = <K extends keyof S>(stateKey: K) => {
    return () => {
      const s = getLastState();
      if (typeof s[stateKey] !== "boolean") {
        throw "stato : m.toggle , key is not boolean";
      }
      commit({
        logName: `toggle ${stateKey}`,
        payload: undefined,
        state: {
          ...s,
          [stateKey]: !s[stateKey],
        },
      });
    };
  };

  const toggleAll = <T>(stateKey: keyof S, key: keyof T) => {
    return (isOn: boolean) => {
      const s = getLastState();
      commit({
        logName: `toggle all items.${key}`,
        payload: isOn,
        state: {
          ...s,
          [stateKey]: mutationFns.toggleAll<T>(key)(
            // @ts-ignore
            s[stateKey],
            isOn
          ),
        },
      });
    };
  };

  const append = <K extends keyof S>(stateKey: K) => {
    return (payload: S[K]) => {
      const s = getLastState();
      commit({
        logName: `add more items to ${stateKey}`,
        payload,
        state: {
          ...s,
          // @ts-ignore
          [stateKey]: [...s[stateKey], ...payload],
        },
      });
    };
  };

  const prepend = <K extends keyof S>(stateKey: K) => {
    return (payload: S[K]) => {
      const s = getLastState();
      commit({
        logName: `add more items to ${stateKey}`,
        payload,
        state: {
          ...s,
          // @ts-ignore
          [stateKey]: [...payload, ...s[stateKey]],
        },
      });
    };
  };

  const sortBy = <T>(stateKey: keyof S) => {
    return (payload: T[], by: keyof T, dir: SORTING_DIR) => {
      const s = getLastState();
      commit({
        logName: `update on item in ${stateKey} by ${by}`,
        payload,
        state: {
          ...s,
          [stateKey]: mutationFns.sortBy(payload, by, dir === SORTING_DIR.ASC),
        },
      });
    };
  };

  const indexBy = <T>(stateKey: keyof S, by: keyof T) => {
    return (payload: T[]) => {
      const s = getLastState();
      commit({
        logName: `update on item in ${stateKey} by ${by}`,
        payload,
        state: {
          ...s,
          [stateKey]: mutationFns.indexBy<T>(by)(payload),
        },
      });
    };
  };

  return {
    updateOneBy,
    updateManyBy,
    set,
    always,
    toggle,
    toggleAll,
    append,
    prepend,
    removeBy,
    sortBy,
    indexBy,
  };
};
